/**
 * What the side panel attaches to a question: the page you're on, other tabs added with `@`, and
 * quoted text. Each goes on the user's message as an AI SDK data part (`data-page-context`,
 * `data-quote`), so the thread keeps it and shows it again; the ChatAgent turns them into text for
 * the model (`packages/api/src/domains/chat/agent/shared-context.ts`).
 */

export const PAGE_PART = "page-context";
export const QUOTE_PART = "quote";

/** Most page text sent for one page. The model reads ~5k words of the main content. */
export const MAX_PAGE_CHARS = 30_000;

export type PageContextData = {
  url: string;
  title: string;
  text: string;
  truncated: boolean;
  /** `page`: the tab the panel follows. `tab`: another tab added with `@`. */
  source: "page" | "tab";
};

export type QuoteData = { text: string; url?: string; title?: string };

/** A tab the panel can attach, as `chrome.tabs` describes it. */
export type TabRef = {
  id: number;
  url: string;
  title: string;
  favIconUrl?: string | undefined;
};

/** What `readPage` returns from inside the page. */
export type PageRead = { url: string; title: string; text: string; truncated: boolean };

/**
 * Runs inside the page (`chrome.scripting.executeScript`), so it must not use anything from
 * outside its own body. Takes the main content when the page marks it (article, main), otherwise
 * the body, as the text a reader sees (`innerText` skips hidden elements, scripts and styles).
 */
export function readPage(maxChars: number): PageRead {
  const main = document.querySelector<HTMLElement>("article, main, [role='main']");
  const root = main && main.innerText.trim().length > 400 ? main : document.body;
  const raw = (root?.innerText ?? "")
    .replace(/[ \t ]+/g, " ")
    .replace(/\n\s*\n\s*\n+/g, "\n\n")
    .trim();
  return {
    url: location.href,
    title: document.title,
    text: raw.slice(0, maxChars),
    truncated: raw.length > maxChars,
  };
}

/** Pages Chrome never lets an extension read (its own pages, the web store, other extensions). */
export function isReadableUrl(url: string | undefined): boolean {
  if (!url) return false;
  if (!/^(https?|file):/.test(url)) return false;
  return !/^https:\/\/(chrome\.google\.com\/webstore|chromewebstore\.google\.com)/.test(url);
}

/** A short label for a tab: its title, or its host when it has none. */
export function tabLabel(tab: { title?: string | undefined; url?: string | undefined }): string {
  const title = tab.title?.trim();
  if (title) return title;
  try {
    return new URL(tab.url ?? "").host || "Untitled";
  } catch {
    return "Untitled";
  }
}

type DataPart = { type: `data-${string}`; data: unknown };
type MessageWithParts = { parts: readonly { type: string }[] };

/**
 * Puts the attached pages and quote in front of the message's own parts, so they show above the
 * question and the model reads them first. A message that already carries context (an edit of an
 * earlier one) keeps its own.
 */
export function withContextParts<T extends MessageWithParts>(
  message: T,
  pages: readonly PageContextData[],
  quote: QuoteData | null,
): T {
  if (message.parts.some((p) => p.type === `data-${PAGE_PART}` || p.type === `data-${QUOTE_PART}`))
    return message;
  const extra: DataPart[] = [
    ...pages.map((data) => ({ type: `data-${PAGE_PART}` as const, data })),
    ...(quote?.text.trim() ? [{ type: `data-${QUOTE_PART}` as const, data: quote }] : []),
  ];
  if (!extra.length) return message;
  return { ...message, parts: [...extra, ...message.parts] };
}
